import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Dialog } from 'primeng/dialog';

import {
  AppointmentDetailsForUserResponse,
  AppointmentsClient,
} from '../../model/fss.api';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../ui/button/button';
import { XClose } from '../../icons/x-close/x-close';
import { SlashCircle01 } from '../../icons/slash-circle-01/slash-circle-01';
import { Calendar } from '../../icons/calendar/calendar';
import { Clock } from '../../icons/clock/clock';
import { Hourglas03 } from '../../icons/hourglas-03/hourglas-03';
import { AlertTriangle } from '../../icons/alert-triangle/alert-triangle';
import { InfoCircle } from '../../icons/info-circle/info-circle';
import { AlertService } from '../../services/alert.service';
import { LoaderService } from '../loader/loader.service';

import { getDateFormatedSrLong } from '../../services/date.service';

@Component({
  standalone: true,
  selector: 'app-client-appointment-cancel-dialog',
  imports: [
    Dialog,
    ButtonComponent,
    XClose,
    SlashCircle01,
    Calendar,
    Clock,
    Hourglas03,
    AlertTriangle,
    InfoCircle,
  ],
  templateUrl: './client-appointment-cancel-dialog.html',
  styleUrl: './client-appointment-cancel-dialog.scss',
})
export class ClientAppointmentCancelDialog {
  private readonly _appointments = inject(AppointmentsClient);
  private readonly _alert = inject(AlertService);
  private readonly _loader = inject(LoaderService);

  visible = model.required<boolean>();
  appointmentId = input<string | undefined>(undefined);

  cancelled = output<string>();

  details = signal<AppointmentDetailsForUserResponse | undefined>(undefined);

  isShortNotice = computed<boolean>(() => this.details()?.isShortNotice ?? false);

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  constructor() {
    effect(() => {
      const id = this.appointmentId();
      const open = this.visible();
      if (open && id) {
        this._load(id);
      } else if (!open) {
        this.details.set(undefined);
      }
    });
  }

  formatLongSrDate(date: Date): string {
    return getDateFormatedSrLong(date);
  }

  formatTimeRange(from: Date, to: Date): string {
    const fmt = (d: Date) =>
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${fmt(from)} - ${fmt(to)}`;
  }

  onCancel(): void {
    this.visible.set(false);
  }

  async onConfirm(): Promise<void> {
    const id = this.appointmentId();
    if (!id) return;
    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    const res = await firstValueFrom(this._appointments.cancelAppointment(id));
    this._alert.addSuccessMsg(res.result);
    this.cancelled.emit(id);
    this.visible.set(false);
  }

  private async _load(id: string): Promise<void> {
    const res = await firstValueFrom(this._appointments.getAppointmentDetailsForUser(id));
    this.details.set(res.result);
  }
}
